import React, { useEffect, useState } from "react";
import { api, formatError } from "@/lib/api";
import { exportCSV, can } from "@/lib/helpers";
import { useAuth } from "@/contexts/AuthContext";
import {
  PageHeader, BtnPrimary, BtnSecondary, BtnGhost, Modal, Field, Input, Textarea, Empty,
} from "@/components/primitives";
import { toast } from "sonner";
import { Plus, DownloadSimple, PencilSimple, Trash } from "@phosphor-icons/react";

const BLANK = {
  code: "", name: "", contact_person: "", email: "", phone: "",
  address: "", payment_terms: "Net 30", notes: "", active: true,
};

export default function Suppliers() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(BLANK);
  const canEdit = can(user.role, "manager");
  const canDelete = can(user.role, "manager");

  async function load() {
    try { const { data } = await api.get("/suppliers"); setRows(data); }
    catch (e) { toast.error(formatError(e)); }
  }
  useEffect(() => { load(); }, []);

  function openCreate() { setEditing(null); setForm(BLANK); setOpen(true); }
  function openEdit(s) { setEditing(s); setForm({ ...BLANK, ...s }); setOpen(true); }
  async function save(e) {
    e.preventDefault();
    try {
      const payload = { ...form };
      // Auto-generate a code from the name if left blank
      if (!payload.code && payload.name) {
        payload.code = payload.name.replace(/[^A-Za-z0-9]/g, "").toUpperCase().slice(0, 12) || "SUP";
      }
      if (editing) await api.put(`/suppliers/${editing.id}`, payload);
      else await api.post("/suppliers", payload);
      toast.success("Saved"); setOpen(false); load();
    } catch (e) { toast.error(formatError(e)); }
  }
  async function remove(s) {
    if (!window.confirm(`Delete ${s.name}?`)) return;
    try { await api.delete(`/suppliers/${s.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatError(e)); }
  }

  return (
    <div data-testid="suppliers-page">
      <PageHeader
        overline="Module / Master Data"
        title="Suppliers"
        subtitle="Reusable supplier list. Used as a dropdown when raising purchase orders."
        actions={
          <>
            <BtnSecondary onClick={() => exportCSV(rows, `suppliers-${Date.now()}.csv`)}>
              <DownloadSimple size={14} weight="bold" className="inline mr-1.5" /> CSV
            </BtnSecondary>
            {canEdit && (
              <BtnPrimary onClick={openCreate} data-testid="supplier-create">
                <Plus size={14} weight="bold" className="inline mr-1.5" /> Add Supplier
              </BtnPrimary>
            )}
          </>
        }
      />
      <div className="phizo-card">
        {rows.length === 0 ? <div className="p-5"><Empty label="No suppliers yet." /></div> : (
          <table className="phizo-table">
            <thead>
              <tr><th>Code</th><th>Name</th><th>Contact</th><th>Phone</th><th>Email</th><th>Terms</th><th>Status</th><th></th></tr>
            </thead>
            <tbody>
              {rows.map((s) => (
                <tr key={s.id} data-testid={`supplier-row-${s.code}`}>
                  <td className="mono font-semibold">{s.code}</td>
                  <td className="font-medium">{s.name}</td>
                  <td className="text-sm">{s.contact_person || "—"}</td>
                  <td className="text-sm mono">{s.phone || "—"}</td>
                  <td className="text-sm mono text-[#52525b]">{s.email || "—"}</td>
                  <td className="text-sm">{s.payment_terms || "—"}</td>
                  <td>
                    <span className="phizo-status" style={{ background: s.active ? "#00C853" : "#a1a1aa", color: s.active ? "#09090b" : "#fff", borderColor: s.active ? "#00C853" : "#a1a1aa" }}>
                      {s.active ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td className="text-right whitespace-nowrap">
                    {canEdit && <BtnGhost onClick={() => openEdit(s)}><PencilSimple size={12} weight="bold" /></BtnGhost>}
                    {canDelete && <BtnGhost onClick={() => remove(s)} className="text-[#FF2A2A]"><Trash size={12} weight="bold" /></BtnGhost>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Modal open={open} onClose={() => setOpen(false)} title={editing ? "Edit Supplier" : "Add Supplier"} wide>
        <form onSubmit={save} className="grid grid-cols-2 gap-3">
          <Field label="Code" hint="Auto-generated from name if left blank">
            <Input disabled={!!editing} data-testid="supplier-code"
              value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} />
          </Field>
          <Field label="Name">
            <Input required data-testid="supplier-name"
              value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </Field>
          <Field label="Contact Person"><Input value={form.contact_person} onChange={(e) => setForm({ ...form, contact_person: e.target.value })} /></Field>
          <Field label="Phone"><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></Field>
          <Field label="Email"><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></Field>
          <Field label="Payment Terms"><Input value={form.payment_terms} onChange={(e) => setForm({ ...form, payment_terms: e.target.value })} /></Field>
          <div className="col-span-2"><Field label="Address"><Textarea value={form.address || ""} onChange={(e) => setForm({ ...form, address: e.target.value })} /></Field></div>
          <div className="col-span-2"><Field label="Notes"><Textarea value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></Field></div>
          <label className="col-span-2 flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} />
            Active (show in dropdowns)
          </label>
          <div className="col-span-2 flex justify-end gap-2 pt-3 border-t border-[#e4e4e7]">
            <BtnSecondary type="button" onClick={() => setOpen(false)}>Cancel</BtnSecondary>
            <BtnPrimary type="submit" data-testid="supplier-save">Save</BtnPrimary>
          </div>
        </form>
      </Modal>
    </div>
  );
}
